import { useMemo } from "react";
import { Card, Col, Empty, List, Progress, Row, Statistic, Table, Tag, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  type AuditLog,
  type ScraperRunWithChannel,
  auditLogsApi,
  channelScrapersApi,
  channelsApi,
  statsApi,
} from "../api/resources";

// CDF (中免) red-led palette — mirrors the H5 / 小程序 surfaces.
const BRAND = {
  red: "#C8102E",
  redSoft: "#FCF2F3",
  blue: "#2E5D8A",
  green: "#2E9B5B",
  gold: "#C9A227",
  orange: "#FF8F1F",
  muted: "#999999",
  grid: "#EEEEEE",
};

// ─── deterministic 30-day price trend (no real API yet) ────────────────────
// Math.sin keeps the curve stable across renders so designers can preview the
// component without flicker. Replace with a real time-series API when ready.
const TREND = Array.from({ length: 30 }, (_, i) => ({
  day: "D" + i,
  price: Math.round(380 + Math.sin(i / 3) * 40 + i * 2),
}));

const CHANNEL_TYPE_LABEL: Record<string, string> = {
  duty_free: "免税店",
  official: "品牌官方",
  brand_authorized: "品牌授权",
  self_operated: "自营平台",
  third_party: "第三方平台",
};

const PIE_COLORS = [BRAND.red, BRAND.gold, BRAND.green, BRAND.blue, "#E0728A", BRAND.muted];

// Shared card surface: white, rounded, soft shadow, light header divider.
const CARD_STYLES = {
  body: { padding: 20 },
  header: { borderBottom: `1px solid ${BRAND.grid}`, minHeight: 48, padding: "0 20px" },
} as const;
const CARD_STYLE = {
  borderRadius: 12,
  border: `1px solid ${BRAND.grid}`,
  boxShadow: "0 1px 2px rgba(26,26,26,0.04), 0 2px 8px rgba(26,26,26,0.05)",
} as const;

function DeltaTag({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat ? BRAND.muted : up ? BRAND.red : BRAND.green;
  return (
    <span style={{ fontSize: 12, marginTop: 6, color, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {flat ? null : up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      周环比 {up ? "+" : ""}
      {pct}%
    </span>
  );
}

function KpiCard({
  title,
  value,
  loading,
  icon,
  accent,
  delta,
}: {
  title: string;
  value: number;
  loading: boolean;
  icon: React.ReactNode;
  accent: string;
  delta?: number | null;
}) {
  return (
    <Card styles={{ body: { padding: 20 } }} style={CARD_STYLE}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Statistic title={title} value={value} loading={loading} valueStyle={{ color: "#1A1A1A", fontWeight: 600 }} />
          {delta !== undefined ? <DeltaTag pct={delta} /> : null}
        </div>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: `${accent}14`,
            color: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  // ─── KPI: week-over-week snapshot ──────────────────────────────────────
  const wowQuery = useQuery({
    queryKey: ["dashboard", "stats-wow"],
    queryFn: () => statsApi.wow(),
  });
  const wow = wowQuery.data;

  // ─── channel distribution (group all channels by type) ─────────────────
  const allChannelsQuery = useQuery({
    queryKey: ["dashboard", "channels-all"],
    queryFn: () => channelsApi.list({ pageSize: 200 }),
  });

  const channelDistribution = useMemo(() => {
    const items = allChannelsQuery.data?.items ?? [];
    const byType = new Map<string, number>();
    for (const c of items) {
      byType.set(c.type, (byType.get(c.type) ?? 0) + 1);
    }
    return Array.from(byType.entries()).map(([type, value]) => ({
      type,
      name: CHANNEL_TYPE_LABEL[type] ?? type,
      value,
    }));
  }, [allChannelsQuery.data]);

  // ─── last 10 audit logs ────────────────────────────────────────────────
  const auditLogsQuery = useQuery({
    queryKey: ["dashboard", "audit-logs"],
    queryFn: () => auditLogsApi.list({ pageSize: 10 }),
  });

  // ─── last 10 scraper runs (global) ─────────────────────────────────────
  const recentRunsQuery = useQuery({
    queryKey: ["dashboard", "scraper-runs"],
    queryFn: () => channelScrapersApi.recentRuns({ pageSize: 10 }),
  });

  // Derive a采集成功率 from the recent runs we already fetched.
  const runStats = useMemo(() => {
    const runs = recentRunsQuery.data?.items ?? [];
    const total = runs.length;
    const success = runs.filter((r) => r.status === "success").length;
    const failed = runs.filter((r) => r.status === "failed").length;
    const rate = total === 0 ? null : Math.round((success / total) * 100);
    return { total, success, failed, rate };
  }, [recentRunsQuery.data]);

  // Lightweight 待办 cards — placeholders backed by what we already know.
  const todos = useMemo(
    () => [
      {
        key: "match",
        icon: <ClockCircleOutlined />,
        color: BRAND.gold,
        title: "待匹配队列",
        desc: "渠道商品待关联标准商品",
        count: "—",
        to: "/channel-products-matrix",
      },
      {
        key: "failed",
        icon: <WarningOutlined />,
        color: BRAND.red,
        title: "采集失败渠道",
        desc: "最近运行中失败的渠道",
        count: runStats.failed,
        to: "/channel-scrapers",
      },
      {
        key: "feedback",
        icon: <ClockCircleOutlined />,
        color: BRAND.blue,
        title: "待处理反馈",
        desc: "来自 H5 / 小程序的用户反馈",
        count: "—",
        to: "/feedback",
      },
    ],
    [runStats.failed],
  );

  // ─── render ────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          运营概览
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: "4px 0 0", fontSize: 13 }}>
          比个价 · Glow Price 全局数据大盘，覆盖商品、渠道、采集与运营。
        </Typography.Paragraph>
      </div>

      {/* Top row: 4 KPI cards — all backed by /api/admin/stats/wow */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <KpiCard
            title="标准商品"
            value={wow?.products.total ?? 0}
            loading={wowQuery.isLoading}
            icon={<ShoppingOutlined />}
            accent={BRAND.red}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <KpiCard
            title="渠道数"
            value={wow?.channels.total ?? 0}
            loading={wowQuery.isLoading}
            icon={<ShopOutlined />}
            accent={BRAND.blue}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <KpiCard
            title="24h 快照"
            value={wow?.snapshots24h.total ?? 0}
            loading={wowQuery.isLoading}
            icon={<LineChartOutlined />}
            accent={BRAND.gold}
            delta={wow?.snapshots24h.deltaPct ?? null}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <KpiCard
            title="活跃采集器"
            value={wow?.activeScrapers.total ?? 0}
            loading={wowQuery.isLoading}
            icon={<SyncOutlined />}
            accent={BRAND.green}
          />
        </Col>
      </Row>

      {/* Todo cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {todos.map((t) => (
          <Col xs={24} sm={8} key={t.key}>
            <Link to={t.to}>
              <Card styles={{ body: { padding: 18 } }} style={CARD_STYLE} hoverable>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: `${t.color}14`,
                      color: t.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                    }}
                  >
                    {t.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: BRAND.muted }}>{t.title}</div>
                    <div style={{ fontSize: 22, fontWeight: 600, color: "#1A1A1A", lineHeight: 1.2 }}>
                      {t.count}
                    </div>
                    <div style={{ fontSize: 12, color: BRAND.muted }}>{t.desc}</div>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {/* Middle row: trend chart + channel distribution + 采集成功率 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="30 天价格趋势" styles={CARD_STYLES} style={CARD_STYLE}>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={TREND} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke={BRAND.grid} vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: BRAND.muted }}
                    axisLine={{ stroke: BRAND.grid }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: BRAND.muted }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${BRAND.grid}`,
                      boxShadow: "0 4px 12px rgba(26,26,26,0.08)",
                    }}
                    labelStyle={{ color: BRAND.muted }}
                  />
                  <Line type="monotone" dataKey="price" stroke={BRAND.red} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title="渠道类型分布"
            styles={CARD_STYLES}
            style={CARD_STYLE}
            loading={allChannelsQuery.isLoading}
          >
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={channelDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={84}
                    label
                  >
                    {channelDistribution.map((entry, idx) => (
                      <Cell key={entry.type} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={4}>
          <Card title="采集成功率" styles={CARD_STYLES} style={CARD_STYLE} loading={recentRunsQuery.isLoading}>
            <div
              style={{
                height: 260,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              {runStats.rate == null ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无运行" />
              ) : (
                <>
                  <Progress
                    type="dashboard"
                    percent={runStats.rate}
                    strokeColor={runStats.rate >= 80 ? BRAND.green : runStats.rate >= 50 ? BRAND.orange : BRAND.red}
                    size={120}
                  />
                  <div style={{ textAlign: "center", fontSize: 12, color: BRAND.muted }}>
                    最近 {runStats.total} 次 · 成功 {runStats.success} / 失败 {runStats.failed}
                  </div>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Bottom row: scraper runs + audit logs */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="最近采集运行" styles={CARD_STYLES} style={CARD_STYLE}>
            <Table<ScraperRunWithChannel>
              size="small"
              rowKey="id"
              pagination={false}
              loading={recentRunsQuery.isLoading}
              dataSource={recentRunsQuery.data?.items ?? []}
              columns={[
                {
                  title: "渠道",
                  dataIndex: "channelName",
                  width: 130,
                  render: (name: string | null, row) =>
                    name ?? (
                      <span style={{ color: BRAND.muted, fontSize: 11 }}>
                        {row.channelScraperId.slice(0, 8)}
                      </span>
                    ),
                },
                {
                  title: "状态",
                  dataIndex: "status",
                  width: 80,
                  render: (s: ScraperRunWithChannel["status"]) => (
                    <Tag color={s === "success" ? "success" : s === "failed" ? "error" : "processing"}>
                      {s}
                    </Tag>
                  ),
                },
                {
                  title: "时间",
                  dataIndex: "startedAt",
                  width: 150,
                  render: (v: string) => (
                    <span style={{ fontSize: 12, color: BRAND.muted }}>
                      {new Date(v).toLocaleString()}
                    </span>
                  ),
                },
                { title: "快照", dataIndex: "insertedCount", width: 60, align: "right" },
                { title: "价格变化", dataIndex: "priceChangesCount", width: 80, align: "right" },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="最近审计日志" styles={CARD_STYLES} style={CARD_STYLE}>
            <List<AuditLog>
              size="small"
              loading={auditLogsQuery.isLoading}
              dataSource={auditLogsQuery.data?.items ?? []}
              renderItem={(log) => (
                <List.Item style={{ paddingInline: 0 }}>
                  <div style={{ display: "flex", width: "100%", alignItems: "center", gap: 8 }}>
                    <Tag bordered={false} style={{ marginInlineEnd: 0 }}>
                      {log.entityType}
                    </Tag>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        {log.adminUsername}
                      </Typography.Text>{" "}
                      <Typography.Text style={{ fontSize: 13 }}>{log.action}</Typography.Text>
                    </span>
                    <span style={{ fontSize: 11, color: BRAND.muted, flexShrink: 0 }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </span>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
